/* eslint-disable react/jsx-props-no-spreading */
import './Select.scss';

import PropTypes from 'prop-types';
import React, {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReactComponent as ArrowIcon } from '@/common/assets/icons/arrow-list.svg';

const Select = ({
  className,
  context,
  disabled,
  icon,
  id,
  name,
  onBlur,
  onChange,
  onClick,
  options,
  placeholder,
  readOnly,
  required,
  value,
}) => {
  const selectRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const classNames = useMemo(
    () =>
      [
        'select',
        disabled || readOnly ? 'select--disabled' : '',
        isOpen && !disabled && !readOnly ? 'select--open' : '',
        required ? 'select--required' : '',
        className,
      ]
        .filter((val) => val)
        .join(' '),
    [className, disabled, readOnly, required, isOpen],
  );

  const handleClickOutside = useCallback(
    (e) => {
      if (selectRef.current && selectRef.current.contains(e.target)) {
        return;
      }
      setIsOpen(false);
      onClick();
    },
    [onClick],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  const handleClick = useCallback(() => {
    setIsOpen(!isOpen);
    onClick();
  }, [isOpen, onClick, setIsOpen]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.code === 'Enter') {
        setIsOpen(!isOpen);
      }
    },
    [isOpen, setIsOpen],
  );

  const handleChange = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onChange(e);
      setIsOpen(false);
    },
    [onChange],
  );

  const Icon = useMemo(() => {
    if (icon) {
      return cloneElement(icon, { className: 'select__arrow' });
    }
    return <ArrowIcon className="select__arrow" />;
  }, [icon]);

  const conditionalProps = useMemo(
    () => ({
      ...(id ? { id } : {}),
      ...(context
        ? { 'data-qa-id': `${context}-${disabled ? 'disabled' : 'active'}` }
        : {}),
    }),
    [context, disabled, id],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <div className={classNames} ref={selectRef} tabIndex="0" onBlur={onBlur}>
      <button
        aria-disabled={disabled}
        className="select__head"
        {...conditionalProps}
        name={name}
        onClick={handleClick}
        onKeyPress={handleKeyPress}
        type="button"
      >
        {!value ? (
          <span className="select__value">{placeholder}</span>
        ) : (
          options.reduce(
            (acc, option, index) =>
              (!acc && option.value === value && (
                // eslint-disable-next-line react/no-array-index-key
                <span className="select__value" key={`select-value-${index}`}>
                  {option.key}
                </span>
              )) ||
              acc,
            null,
          )
        )}
        <>{Icon}</>
      </button>
      <ul
        aria-activedescendant={value}
        className="select__list"
        data-qa-id="select-list"
        role="listbox"
        tabIndex="0"
      >
        {options.map((option, index) => (
          <li
            aria-selected={option.value === value}
            className={`select__item ${
              option.value === value ? 'select__item--selected' : ''
            }`}
            data-qa-id="select-item"
            id={`select-item-${index}`}
            key={`select-item-${index}`}
            role="option"
          >
            <button
              className={`select__option ${
                option.value === value ? 'select__option--selected' : ''
              }`}
              data-qa-id="select-option"
              data-qa-value={option.value}
              disabled={disabled}
              name={name}
              onClick={handleChange}
              onKeyPress={handleChange}
              type="button"
              value={option.value}
            >
              {option.key}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

Select.defaultProps = {
  className: '',
  context: null,
  disabled: false,
  icon: null,
  id: null,
  name: null,
  onBlur: () => {},
  onChange: () => {},
  onClick: () => {},
  placeholder: '',
  readOnly: false,
  required: false,
  value: '1',
};

Select.propTypes = {
  className: PropTypes.string,
  context: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.element,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Select;
